import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Tag } from '../../../core/models/tag.model';
import { Team, TeamMember, TeamRole } from '../../../core/models/team.model';
import { LoggerService } from '../../../core/services/logger.service';
import { TagService } from '../../../core/services/tag.service';
import { TeamService } from '../../../core/services/team.service';
import { TranslationService } from '../../../core/services/translation.service';
import { OrganizationUser, UserService } from '../../../core/services/user.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../../shared/components/modal-dialog/modal-dialog.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

interface TeamFormModel {
  name: string;
  description: string;
  default_tag_id: string;
}

/** Admin CRUD for teams and their membership (`team.manage`) — design doc §6.5. */
@Component({
  selector: 'app-teams-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    BackButtonComponent,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ModalDialogComponent,
    TranslatePipe,
  ],
  templateUrl: './teams-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './teams-settings.component.css',
})
export class TeamsSettingsComponent implements OnInit {
  private teamService = inject(TeamService);
  private tagService = inject(TagService);
  private userService = inject(UserService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('teams-settings');

  teams!: Signal<Team[]>;
  tags!: Signal<Tag[]>;
  orgUsers!: Signal<OrganizationUser[]>;
  tagLabels = computed(() => new Map(this.tags().map((tag) => [tag.id, tag.label])));

  expandedTeamId = signal<string | null>(null);
  members = signal<TeamMember[]>([]);

  addMemberForm = new FormGroup({});
  addMemberModel: { user_id: string } = { user_id: '' };
  addMemberFields: FormlyFieldConfig[] = [];

  showEditor = signal(false);
  editing = signal<Team | null>(null);
  saving = signal(false);
  error = signal<string | null>(null);

  form = new FormGroup({});
  model: TeamFormModel = { name: '', description: '', default_tag_id: '' };
  fields: FormlyFieldConfig[] = [];

  ngOnInit(): void {
    this.teams = this.teamService.createQuery().data;
    this.tags = this.tagService.createActiveQuery().data;
    this.orgUsers = this.userService.createQuery(false).data;

    const t = (key: string) => this.translate.translate(key);
    this.fields = [
      { key: 'name', type: 'text', props: { label: t('settings.teams.form.name'), required: true } },
      { key: 'description', type: 'textarea', props: { label: t('settings.teams.form.description') } },
      {
        key: 'default_tag_id',
        type: 'native-select',
        props: {
          label: t('settings.teams.form.defaultTag'),
          showLabel: true,
          options: [
            { value: '', label: t('settings.teams.form.noDefaultTag') },
            ...this.tags().map((tag) => ({ value: tag.id, label: tag.label })),
          ],
        },
      },
    ];
  }

  openCreate(): void {
    this.editing.set(null);
    this.model = { name: '', description: '', default_tag_id: '' };
    this.error.set(null);
    this.showEditor.set(true);
  }

  openEdit(team: Team): void {
    this.editing.set(team);
    this.model = { name: team.name, description: team.description, default_tag_id: team.default_tag_id };
    this.error.set(null);
    this.showEditor.set(true);
  }

  closeEditor(): void {
    this.showEditor.set(false);
  }

  async save(): Promise<void> {
    this.saving.set(true);
    this.error.set(null);
    try {
      const existing = this.editing();
      if (existing) {
        await this.teamService.update({ id: existing.id, ...this.model });
      } else {
        await this.teamService.create(this.model);
      }
      this.showEditor.set(false);
    } catch (err) {
      this.log.error('Failed to save team', err);
      this.error.set(this.translate.translate('settings.teams.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  async toggleActive(team: Team): Promise<void> {
    try {
      await this.teamService.update({ id: team.id, is_active: team.is_active ? 0 : 1 });
    } catch (err) {
      this.log.error('Failed to toggle team active state', err);
    }
  }

  async toggleMembers(team: Team): Promise<void> {
    if (this.expandedTeamId() === team.id) {
      this.expandedTeamId.set(null);
      return;
    }
    this.expandedTeamId.set(team.id);
    this.members.set(await this.teamService.getMembers(team.id));
    this.buildAddMemberFields();
  }

  memberName(userId: string): string {
    const user = this.orgUsers().find((u) => u.id === userId);
    return user?.full_name || user?.email || userId;
  }

  /** Org users not already an active member of the currently-expanded team. */
  availableUsers(): OrganizationUser[] {
    const memberIds = new Set(this.members().map((m) => m.user_id));
    return this.orgUsers().filter((u) => !memberIds.has(u.id));
  }

  private buildAddMemberFields(): void {
    this.addMemberModel = { user_id: '' };
    this.addMemberFields = [
      {
        key: 'user_id',
        type: 'native-select',
        props: {
          label: this.translate.translate('settings.teams.selectUser'),
          options: [
            { value: '', label: this.translate.translate('settings.teams.selectUser') },
            ...this.availableUsers().map((u) => ({ value: u.id, label: u.full_name || u.email })),
          ],
        },
      },
    ];
  }

  async addMember(teamId: string): Promise<void> {
    if (!this.addMemberModel.user_id) return;
    try {
      await this.teamService.addMember(teamId, this.addMemberModel.user_id, 'member');
      this.members.set(await this.teamService.getMembers(teamId));
      this.buildAddMemberFields();
    } catch (err) {
      this.log.error('Failed to add team member', err);
    }
  }

  async setMemberRole(member: TeamMember, role: TeamRole): Promise<void> {
    try {
      await this.teamService.setMemberRole(member.id, role);
      this.members.update((list) => list.map((m) => (m.id === member.id ? { ...m, team_role: role } : m)));
    } catch (err) {
      this.log.error('Failed to change member role', err);
    }
  }

  async removeMember(member: TeamMember): Promise<void> {
    try {
      await this.teamService.removeMember(member.id);
      this.members.update((list) => list.filter((m) => m.id !== member.id));
      this.buildAddMemberFields();
    } catch (err) {
      this.log.error('Failed to remove team member', err);
    }
  }
}
